import SideModal from "components/SideModal";
import { useTranslation } from "contexts/i18nContext";
import { useToast } from "contexts/ToastContext";
import { ChangeEvent, useRef, useState } from "react";
import { addComplaintEvidence } from "services/prosecutorDecipilineService";
import { CollectedEvidence, Complaints } from "types";
import { LoadingIcon } from "utils/constant";

interface EvidenceModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  complaintData: Complaints;
  onRefresh: any;
}

export default function AddEvidenceModal({
  isOpen,
  setIsOpen,
  complaintData,
  onRefresh,
}: EvidenceModalProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [evidence, setEvidence] = useState<CollectedEvidence>({
    Collected_by: "",
    Evidence_type: "",
    Collected_date: "",
    Remark: "",
    attachments: [],
    complaint: null,
    files: [] as File[],
  });

  const handleBlur = (field: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!evidence.Collected_by.trim()) {
      errors.Collected_by = t("collectorNameRequired");
    }

    if (!evidence.Evidence_type.trim()) {
      errors.Evidence_type = t("evidenceTypeRequired");
    }

    if (!evidence.Collected_date) {
      errors.Collected_date = t("collectionDateRequired");
    }

    return errors;
  };

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setEvidence((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setEvidence((prev) => ({
        ...prev,
        files: [...prev.files, ...newFiles],
      }));
    }
  };

  const removeFile = (index: number) => {
    setEvidence((prev) => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const allFields = ["Collected_by", "Evidence_type", "Collected_date"];
    allFields.forEach((field) =>
      setTouched((prev) => ({ ...prev, [field]: true })),
    );

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      Object.values(errors).forEach((error) => {
        showToast(error, "error");
      });
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();

      Object.entries(evidence).forEach(([key, value]) => {
        if (key === "files") {
          value.forEach((file: File) => {
            formData.append(`evidence_files`, file);
          });
        } else if (value !== null && value !== undefined) {
          formData.append(key, value.toString());
        }
      });

      formData.append("complaint", complaintData?.uu_id?.toString() || "");
      await addComplaintEvidence(formData);

      onRefresh();
      setIsOpen(false);
      showToast(t("evidenceSuccessfullyAdded"), "success");

      setEvidence({
        Collected_by: "",
        Evidence_type: "",
        Collected_date: "",
        Remark: "",
        attachments: [],
        complaint: null,
        files: [] as File[],
      });
      setTouched({});
    } catch (err: any) {
      console.error("Submission error:", err);
      showToast(t("failedToSubmitEvidence"), "error");
    } finally {
      setLoading(false);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  if (!isOpen) return null;

  const errors = validateForm();

  return (
    <SideModal isOpen={isOpen} setIsOpen={setIsOpen} title={t("addEvidence")}>
      <div className="px-1">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Simplified Form Fields */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                {t("collectedBy")} <span className="text-red-600">*</span>
              </label>
              <input
                required
                className={`w-full px-3 py-2 bg-white border rounded text-gray-800 focus:outline-none focus:ring-1 focus:ring-red-800 ${
                  touched.Collected_by && errors.Collected_by
                    ? "border-red-600"
                    : "border-gray-300"
                }`}
                type="text"
                name="Collected_by"
                placeholder={t("collectedByPlaceholder")}
                value={evidence.Collected_by}
                onChange={handleChange}
                onBlur={() => handleBlur("Collected_by")}
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                {t("evidenceType")} <span className="text-red-600">*</span>
              </label>
              <select
                required
                className={`w-full px-3 py-2 bg-white border rounded text-gray-800 focus:outline-none focus:ring-1 focus:ring-red-800 ${
                  touched.Evidence_type && errors.Evidence_type
                    ? "border-red-600"
                    : "border-gray-300"
                }`}
                name="Evidence_type"
                value={evidence.Evidence_type}
                onChange={handleChange}
                onBlur={() => handleBlur("Evidence_type")}
              >
                <option value="">{t("selectType")}</option>
                <option value="Documentary">{t("documentary")}</option>
                <option value="Digital">{t("digital")}</option>
                <option value="Physical">{t("physical")}</option>
                <option value="Other">{t("other")}</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                {t("collectionDate")} <span className="text-red-600">*</span>
              </label>
              <input
                required
                className={`w-full px-3 py-2 bg-white border rounded text-gray-800 focus:outline-none focus:ring-1 focus:ring-red-800 ${
                  touched.Collected_date && errors.Collected_date
                    ? "border-red-600"
                    : "border-gray-300"
                }`}
                type="date"
                name="Collected_date"
                value={evidence.Collected_date}
                onChange={handleChange}
                onBlur={() => handleBlur("Collected_date")}
                max={new Date().toISOString().split("T")[0]}
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                {t("remarks")}
              </label>
              <textarea
                rows={3}
                name="Remark"
                value={evidence.Remark}
                onChange={handleChange}
                placeholder={t("remarkPlaceholder")}
                className="w-full px-3 py-2 bg-white border border-gray-300 rounded text-gray-800 focus:outline-none focus:ring-1 focus:ring-red-800 resize-none"
              />
            </div>
          </div>

          {/* Simplified File Upload */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              {t("evidenceFiles")}
            </label>
            <div className="border border-gray-300 rounded p-4">
              <input
                type="file"
                ref={fileInputRef}
                name="files"
                onChange={handleFileChange}
                multiple
                style={{ display: "none" }}
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              />

              <div className="text-center">
                <button
                  type="button"
                  onClick={triggerFileInput}
                  className="px-4 py-2 bg-gray-800 text-white rounded hover:bg-gray-700 transition-colors"
                >
                  {t("uploadFiles")}
                </button>
                <p className="text-xs text-gray-500 mt-2">
                  {t("fileTypesSimple")}
                </p>
              </div>

              {/* File List */}
              {evidence.files.length > 0 && (
                <div className="mt-4">
                  {evidence.files.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-2 bg-gray-50 rounded mb-2"
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 bg-red-100 rounded flex items-center justify-center">
                          <span className="text-red-600 text-xs font-bold">
                            {file.type.includes("pdf")
                              ? "PDF"
                              : file.type.includes("word")
                                ? "DOC"
                                : file.type.includes("image")
                                  ? "IMG"
                                  : "FILE"}
                          </span>
                        </div>
                        <span className="text-sm text-gray-700 truncate">
                          {file.name.length > 25
                            ? file.name.substring(0, 25) + "..."
                            : file.name}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="text-gray-500 hover:text-red-600"
                        aria-label={t("removeFile")}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-between items-center pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition-colors"
            >
              {t("cancel")}
            </button>

            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-red-800 text-white rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  {t("submitting")}
                </>
              ) : (
                t("addEvidence")
              )}
            </button>
          </div>
        </form>
      </div>
    </SideModal>
  );
}
